<template>
    <div class="newsletter-preferences-form">
        <itsu-errors
            v-if="states.errors.length"
            :errors="states.errors"
        />

        <form
            @submit.prevent="handleSubmit"
            v-test:form
        >
            <fieldset
                :disabled="states.submitting"
                v-test:fieldset
            >
                <fieldset>
                    <legend
                        class="h5"
                        v-test:yourDetailsLabel
                    >
                        {{ $snippet('your_details', 'your details') }}
                    </legend>

                    <select-input
                        name="store_id"
                        :label="$snippet('local_store_label', 'select your local itsu')"
                        :placeholder="$snippet('select_input_placeholder', 'please select…')"
                        v-model="formData.store_id"
                        :options="itsuStoreOptions"
                        v-test:storeSelect
                    />

                    <text-input
                        name="firstname"
                        :label="$snippet('firstname_label', 'first name')"
                        :placeholder="$snippet('text_input_placeholder')"
                        v-model="formData.firstname"
                        v-test:firstnameInput
                    />

                    <text-input
                        name="lastname"
                        :label="$snippet('lastname_label', 'last name')"
                        :placeholder="$snippet('text_input_placeholder')"
                        v-model="formData.lastname"
                        v-test:lastnameInput
                    />

                    <email-input
                        name="email"
                        :label="$snippet('email_label', 'your email')"
                        :placeholder="$snippet('email_input_placeholder')"
                        required
                        v-model="formData.email"
                        v-test:emailInput
                    />
                </fieldset>

                <fieldset>
                    <legend
                        class="h5"
                        v-test:interestsLabel
                    >
                        {{ $snippet('interests_label', 'i\'m interested in') }}
                    </legend>

                    <b-form-checkbox-group
                        name="interests"
                        v-model="formData.interests"
                    >
                        <label
                            v-for="interest in interests"
                            :key="interest.value"
                            class="interest-item"
                            :aria-label="$snippet('interest', 'interest')"
                        >
                            <lazy-image
                                class="interest-icon"
                                :src="interest.icon.src"
                                :alt="interest.icon.alt"
                                v-test:interestIcon
                            />

                            <span
                                class="interest-text"
                                v-test:interestText
                            >
                                {{ interest.name }}
                            </span>

                            <b-form-checkbox
                                class="interest-checkbox"
                                :value="interest.key"
                            />
                        </label>
                    </b-form-checkbox-group>
                </fieldset>

                <transition name="fade-in-up">
                    <div
                        v-if="canSubmit"
                        class="submit-button"
                    >
                        <b-button
                            variant="primary"
                            type="submit"
                            v-test:submitButton
                        >
                            {{ submitButtonText }}
                        </b-button>
                    </div>
                </transition>
            </fieldset>
        </form>
    </div>
</template>

<script>
    import { mapActions } from 'vuex';
    import ItsuErrors from '../ItsuErrors/ItsuErrors';
    import EmailInput from '../../atoms/inputs/EmailInput/EmailInput';
    import TextInput from '../../atoms/inputs/TextInput/TextInput';
    import SelectInput from '../../atoms/inputs/SelectInput/SelectInput';
    import { BFormCheckbox, BFormCheckboxGroup } from 'bootstrap-vue';
    import { ITSU_NEWSLETTER_CTC_ID_COOKIE_NAME } from '../../../plugins/constants';

    export default {
        name: 'newsletter-preferences-form',

        components: {
            SelectInput,
            TextInput,
            EmailInput,
            ItsuErrors,
            BFormCheckbox,
            BFormCheckboxGroup,
        },

        props: {
            interests: {
                type: Array,
                default: () => ([]),
            },

            locations: {
                type: Array,
                default: () => ([]),
            },

            submitButtonText: {
                type: String,
                required: true,
            },

            contactData: {
                type: Object,
                default: () => ({}),
            },
        },

        data() {
            return {
                formData: {
                    id: null,
                    store_id: null,
                    firstname: '',
                    lastname: '',
                    email: '',
                    interests: [],
                    ...this.contactData,
                },
                states: {
                    submitting: false,
                    errors: [],
                },
            };
        },

        computed: {
            /**
             * Check if the form can be submitted.
             *
             * @returns {boolean}
             */
            canSubmit() {
                return Boolean(this.formData.email);
            },

            /**
             * Map the locations to the select format.
             *
             * @returns {{text: string, value: number|string}[]}
             */
            itsuStoreOptions() {
                return this.locations.map(({ id, name }) => ({
                    text: name,
                    value: id,
                }));
            },
        },

        methods: {
            ...mapActions('atreemo/newsletter', [
                'updatePreferences',
                'signup',
            ]),

            /**
             * Handle the form submission.
             */
            async handleSubmit() {
                if (!this.canSubmit) {
                    return;
                }

                this.states.errors = [];
                this.states.submitting = true;

                try {
                    const request = this.formData.id
                        ? this.updatePreferences
                        : this.signup;

                    const { id } = await request(this.formData);

                    this.$cookies.set(ITSU_NEWSLETTER_CTC_ID_COOKIE_NAME, id);

                    this.$emit('success');
                } catch ({ response }) {
                    this.states.errors = response
                        ?.data
                        ?.error
                        ?.details
                        ?.errors
                        || [
                            this.$snippet('preferences_update_error_fallback', 'there was an issue updating your preferences'),
                        ];
                } finally {
                    this.states.submitting = false;
                }
            },
        },
    };
</script>

<style lang="scss" scoped>
    ::v-deep {
        @import "~bootstrap/scss/custom-forms";
    }

    .fade-in-up-enter-active,
    .fade-in-up-leave-active {
        transition: 0.5s;
    }

    .fade-in-up-enter,
    .fade-in-up-leave-to {
        opacity: 0;
        transform: translateY(5px);
    }

    .submit-button {
        margin-top: 30px;
        text-align: center;
    }

    .btn {
        min-width: 140px;
    }

    .interest-item {
        display: flex;
        align-items: center;
    }

    .interest-icon {
        margin-right: 10px;
        height: 2em;
    }

    .interest-text {
        margin-right: auto;
    }

    fieldset {
        margin-bottom: 2rem;
    }
</style>
